import type { ReactNode } from 'react';
import { ShieldAlert, Radar, Server, BarChart2, Zap } from 'lucide-react';
import { AppLogo } from '@/components/layout/AppLogo';

interface AuthLayoutProps {
  children: ReactNode;
}

export const AuthLayout = ({ children }: AuthLayoutProps) => {
  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[45%] bg-card relative overflow-hidden">
        {/* Background Effects */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/4 -left-20 h-96 w-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 h-80 w-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-64 w-64 bg-white/5 rounded-full blur-2xl" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-16 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/40 rounded-xl blur-md" />
              <div className="relative bg-white/5 border border-white/10 rounded-2xl h-14 w-14 flex items-center justify-center shadow-lg">
                <AppLogo
                  variant="icon"
                  alt="BlueB"
                  className="h-11 w-11 drop-shadow"
                />
              </div>
            </div>
            <div className="flex flex-col">
              <h1 className="text-white text-xl font-semibold tracking-tight">
                BlueB
              </h1>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Incident Tracking System
              </span>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-col gap-8">
            <div>
              <h2 className="text-3xl xl:text-4xl font-semibold text-white tracking-tight leading-tight">
                Defend, detect, and respond — all in one place
              </h2>
              <p className="mt-4 text-muted-foreground text-[15px] leading-relaxed max-w-md">
                BlueB unifies incident response, threat intelligence, asset
                visibility, and analytics so your team can act on what matters
                — fast.
              </p>
            </div>

            {/* Value Highlight */}
            <div className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-primary/10 to-transparent border border-primary/20">
              <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/20">
                <Zap className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-white text-[14px] font-semibold">
                  Faster incident response
                </p>
                <p className="text-muted-foreground text-[12px]">
                  Context-rich alerts, coordinated workflows
                </p>
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="flex flex-col gap-4">
              {[
                {
                  icon: ShieldAlert,
                  label: 'Incident Management',
                  desc: 'Triage, track, and resolve incidents end-to-end',
                },
                {
                  icon: Radar,
                  label: 'Threat Intelligence',
                  desc: 'Curated IOCs and advisories from CERT',
                },
                {
                  icon: Server,
                  label: 'Asset Visibility',
                  desc: 'Inventory, vulnerabilities, and agent telemetry',
                },
                {
                  icon: BarChart2,
                  label: 'Analytics & Reports',
                  desc: 'Posture, exposure, and trend insights',
                },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="flex items-center gap-4 group">
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-white/5 border border-white/10 group-hover:bg-white/10 group-hover:border-primary/30 transition-all duration-200">
                    <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div>
                    <p className="text-white text-[13px] font-medium">
                      {label}
                    </p>
                    <p className="text-muted-foreground text-[12px]">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <p className="text-gray-600 text-[12px]">
            © {new Date().getFullYear()} BlueB. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col items-center justify-center bg-muted/50 dark:bg-card px-6 py-12">
        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center gap-2.5 mb-8">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/40 rounded-lg blur-md" />
            <div className="relative bg-white/5 border border-white/10 rounded-xl h-12 w-12 flex items-center justify-center shadow-lg">
              <AppLogo
                variant="icon"
                alt="BlueB"
                className="h-10 w-10 drop-shadow"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <h1 className="text-foreground dark:text-white text-lg font-semibold tracking-tight">
              BlueB
            </h1>
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">
              Incident Tracking System
            </span>
          </div>
        </div>

        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
};
